/**
 * Normalizes raw customer detail from API for use across DSOK customer-detail subpages.
 * Parses JSON string fields safely and provides consistent shape with defaults.
 *
 * @param {Object} raw - Raw response from getDsokCustomerDetailByUrl / getCustomer
 * @returns {Object} Normalized customer detail
 */
export function normalizeDsokCustomerDetail(raw) {
  if (raw == null || typeof raw !== "object") {
    return {
      id: null,
      companyName: "",
      company_name: "",
      customer_type: null,
      dsok: null,
      url: "",
      dsokSettings: {},
      uiSettings: {},
    };
  }

  // Backend may put main customer fields in a nested "customer" object
  const top = raw.customer != null && typeof raw.customer === "object" ? raw.customer : {};
  const id = raw.id ?? top.id ?? null;
  const url = raw.url ?? top.url ?? top.code ?? "";
  const companyName = raw.companyName ?? raw.company_name ?? top.companyName ?? top.company_name ?? "";

  let dsokSettings = parseJsonField(
    raw.dsokSettings ?? raw.dsok_settings ?? raw.dsok,
    {}
  );
  const kioskSettings = parseJsonField(
    raw.kioskSettings ?? raw.kiosk_settings,
    {}
  );

  // If backend returned no nested customer in dsokSettings, seed from top-level
  const customer = dsokSettings?.customer || {};
  if (Object.keys(customer).length === 0 && (id != null || url)) {
    dsokSettings = {
      ...dsokSettings,
      customer: {
        customerId: id,
        code: url,
        storeCode: raw.storeCode ?? raw.store_code ?? top.storeCode ?? top.store_code ?? "",
      },
    };
  }
  const customerFromSettings = dsokSettings?.customer || {};
  const uiSettings = customerFromSettings?.customerUISettings ?? {};

  return {
    ...raw,
    ...top,
    id,
    companyName,
    company_name: companyName,
    customer_type: raw.customer_type ?? raw.customerType ?? top.customer_type ?? top.customerType ?? null,
    dsok: raw.dsok ?? top.dsok ?? null,
    url,
    dsokSettings: {
      ...dsokSettings,
      customer: { ...customerFromSettings, ...dsokSettings.customer },
    },
    kioskSettings,
    uiSettings,
  };
}

/**
 * @param {*} value - String (JSON) or object
 * @param {Object} defaultValue
 * @returns {Object}
 */
function parseJsonField(value, defaultValue) {
  if (value == null) return defaultValue ?? {};
  if (typeof value === "object") return value;
  if (typeof value !== "string") return defaultValue ?? {};
  try {
    const parsed = JSON.parse(value);
    return parsed != null && typeof parsed === "object" ? parsed : defaultValue ?? {};
  } catch {
    return defaultValue ?? {};
  }
}
